import { v4 as uuidv4 } from 'uuid'
import configs from '@/config'
import cookie from '@/services/cookie'
import { UNLEASH_SESSION_COOKIE, readRawSessionCookie } from './unleashSession.constants'

const TWO_YEARS_IN_DAYS = 730

// Opaque-token guard — mirrors general-api's resolveSessionId. We only bound
// charset (no control chars → no header/log injection) and length; the exact
// shape is irrelevant to Unleash hashing.
const SESSION_ID_REGEX = /^[\w-]{1,64}$/
const isValidSessionId = (value) => typeof value === 'string' && SESSION_ID_REGEX.test(value)

/**
 * Read the shared session cookie; mint + persist one if absent or malformed.
 * READ-THEN-MINT — never overwrites a valid existing value, so visit order
 * (presenter-first vs pricing-first) can't flip the variant.
 *
 * The cookie is written on the environment-driven ROOT domain via
 * cookie.setRootDomainCookie (localhost / ahaslide.com / ahaslides.com), never
 * a hardcoded domain. `Secure` is off only on local (http://localhost); on/off
 * tracks https so the browser actually stores it.
 *
 * @returns {string} the resolved session id
 */
export const ensureUnleashSession = () => {
  const existing = readRawSessionCookie()
  if (isValidSessionId(existing)) return existing

  const minted = uuidv4()
  cookie.setRootDomainCookie(UNLEASH_SESSION_COOKIE, minted, {
    expires: TWO_YEARS_IN_DAYS,
    sameSite: 'Lax',
    secure: configs.env !== 'local',
  })
  return minted
}

/**
 * Tracks the session id to send, starting from the page-load value. general-api
 * resets the cookie to the signed-in user's saved id (BOO-1279), so each call
 * re-reads it and, on a valid change, switches to it and tells the client SDK
 * once. A missing or malformed cookie never replaces the last good id.
 * @param {string} initialSessionId the id resolved at bootstrap
 * @param {{ onChange?: (sessionId: string) => void }} [options]
 * @returns {{ current: () => string }}
 */
export const createSessionIdFollower = (initialSessionId, { onChange } = {}) => {
  let sessionId = initialSessionId
  return {
    current: () => {
      const latest = readRawSessionCookie()
      if (isValidSessionId(latest) && latest !== sessionId) {
        sessionId = latest
        try {
          if (onChange) onChange(latest)
        } catch (error) {
          // The client flags keep the old id until reload; the request itself must still go out.
        }
      }
      return sessionId
    },
  }
}
